package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.data.api.createDefaultApiClient
import com.islamicmedia.shared.domain.model.CancelReasonOption
import com.islamicmedia.shared.presentation.CancelStep
import com.islamicmedia.shared.presentation.CancellationController
import com.islamicmedia.shared.presentation.ReferralCopy
import com.islamicmedia.site.theme.*
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * The in-app cancellation conversation: one survey question, then up to three save offers, then a
 * warm goodbye. Replaces handing people straight to Stripe's hosted portal, where cancelling is a
 * single button on a page we don't own and the reason is never recorded.
 *
 * Every decision — which offers, in what order, what the copy says — comes from
 * [CancellationController] and the shared model. This file only lays it out. It is the web twin of
 * `composeApp/.../ui/components/CancelSubscriptionFlow.kt` (Compose HTML can't share a composable
 * with Material 3), so the two must stay visually consistent — a change here needs the same change
 * there, and vice versa (see CLAUDE.md).
 *
 * [onSwitchToPortal] is the escape hatch for members whose billing we can't write to (ThriveCart's
 * separate Stripe account): the server refuses the flow, and rather than show a dead end we send
 * them to the portal, which always works.
 */
@Composable
fun CancelSubscriptionFlow(
    onDismiss: () -> Unit,
    /** Called once anything has actually changed, so the caller can re-fetch the entitlement. */
    onChanged: () -> Unit,
    onSwitchToPortal: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    val controller = remember { CancellationController(createDefaultApiClient(), scope) }
    val state by controller.state.collectAsState()

    // The survey and offers are admin-editable. This refines what is already on screen from the
    // built-in defaults, so the first question draws immediately and a failed fetch simply shows
    // the shipped flow rather than nothing.
    LaunchedEffect(Unit) { controller.loadConfig() }

    // The server said this member bills somewhere we can't reach. Leave immediately for the portal
    // rather than rendering a step that can't go anywhere.
    LaunchedEffect(state.useBillingPortal) {
        if (state.useBillingPortal) {
            onDismiss()
            onSwitchToPortal()
        }
    }

    // One notification per terminal state, not per recomposition — the profile re-fetches the
    // entitlement on it, and Saved/Farewell both change what the membership card should say.
    LaunchedEffect(state.step) {
        if (state.step is CancelStep.Saved || state.step is CancelStep.Farewell) onChanged()
    }

    // Full-viewport backdrop. z-index 3000 because this opens from inside the Profile screen,
    // above its own 1000-level modals.
    Div(attrs = {
        onClick { onDismiss() }
        style {
            property("position", "fixed")
            property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
            property("background", "rgba(15, 23, 42, 0.55)")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(20.px)
            property("box-sizing", "border-box")
            property("z-index", "3000")
        }
    }) {
        // Card — stop click propagation so taps inside don't dismiss.
        Div(attrs = {
            onClick { event -> event.stopPropagation() }
            style {
                property("position", "relative")
                property("width", "min(94%, 420px)")
                property("max-height", "90vh")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                backgroundColor(Color.white)
                borderRadius(24.px)
                property("box-shadow", CardElevatedShadow)
                padding(28.px)
                property("text-align", "center")
                property("box-sizing", "border-box")
            }
        }) {
            when (val step = state.step) {
                CancelStep.Survey -> SurveyStep(
                    reasons = state.reasons,
                    selected = state.selectedReason,
                    note = state.note,
                    loading = state.loading,
                    onSelect = controller::selectReason,
                    onNote = controller::setNote,
                    onContinue = controller::submitSurvey,
                    onKeep = onDismiss,
                )

                is CancelStep.Offer -> OfferStep(
                    step = step,
                    periodEndIso = state.periodEndIso,
                    referralFreeMonthUntilIso = state.referralFreeMonthUntilIso,
                    loading = state.loading,
                    onAccept = controller::acceptOffer,
                    onDecline = controller::declineOffer,
                )

                CancelStep.FinalConfirm -> FinalConfirmStep(
                    periodEndIso = state.periodEndIso,
                    referralFreeMonthUntilIso = state.referralFreeMonthUntilIso,
                    loading = state.loading,
                    onConfirm = controller::confirmCancel,
                    onKeep = onDismiss,
                )

                is CancelStep.Saved -> SavedStep(step = step, onDone = onDismiss)

                is CancelStep.Farewell -> FarewellStep(step = step, onDone = onDismiss)
            }

            state.error?.let { message ->
                Spacer(14)
                Div(attrs = {
                    style {
                        fontSize(13.px)
                        color(ScoreRed)
                        property("text-align", "center")
                        property("line-height", "18px")
                    }
                }) { Text(message) }
            }
        }
    }
}

// ── Step 1: the survey ──────────────────────────────────────────────────────────────────────

@Composable
private fun SurveyStep(
    reasons: List<CancelReasonOption>,
    selected: CancelReasonOption?,
    note: String,
    loading: Boolean,
    onSelect: (CancelReasonOption) -> Unit,
    onNote: (String) -> Unit,
    onContinue: () -> Unit,
    onKeep: () -> Unit,
) {
    Div(attrs = {
        style {
            fontSize(20.px)
            fontWeight(700)
            color(Gray900)
            property("text-align", "center")
            property("line-height", "26px")
        }
    }) { Text("One question before you go") }
    Spacer(8)
    Div(attrs = {
        style {
            fontSize(14.px)
            color(Gray600)
            property("text-align", "center")
            property("line-height", "20px")
        }
    }) { Text("What's the main reason you're leaving?") }
    Spacer(18)

    reasons.forEach { reason ->
        ReasonRow(reason = reason, isSelected = reason.key == selected?.key, onSelect = { onSelect(reason) })
    }

    // Driven by the config, not by a hardcoded "other": an always-visible free-text box turns a
    // one-tap question into a form, and the answer we most need is the tap, not the essay.
    if (selected?.allowNote == true) {
        Spacer(10)
        TextArea(value = note, attrs = {
            onInput { onNote(it.value) }
            placeholder("Tell us what happened (optional)")
            attr("rows", "3")
            style {
                width(100.percent)
                padding(10.px)
                borderRadius(12.px)
                property("border", "1px solid #E5E5E5")
                property("box-sizing", "border-box")
                property("font-family", "inherit")
                fontSize(14.px)
                property("resize", "vertical")
            }
        })
    }

    Spacer(20)
    PrimaryCta(label = "Continue", enabled = selected != null, loading = loading, onClick = onContinue)
    SecondaryLink("Never mind, keep my subscription", enabled = !loading, onClick = onKeep)
}

@Composable
private fun ReasonRow(reason: CancelReasonOption, isSelected: Boolean, onSelect: () -> Unit) {
    Div(attrs = {
        onClick { onSelect() }
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            padding(13.px, 14.px)
            borderRadius(12.px)
            cursor("pointer")
            property("margin-bottom", "8px")
            property("box-sizing", "border-box")
            if (isSelected) {
                backgroundColor(PrimaryPale)
                property("border", "2px solid ${Primary}")
            } else {
                backgroundColor(Gray100)
                property("border", "1px solid ${Gray200}")
            }
        }
    }) {
        // A real <input type="radio">, not a hand-rolled circle: this is a pick-ONE question, so
        // the native control is both the right semantics and the right look. accent-color paints
        // it in the app's teal without reimplementing it. pointer-events:none hands every click to
        // the row, which is already the target — otherwise the input swallows taps on itself and
        // the row stops responding where the user aims. Mirrors composeApp's RadioButton.
        RadioInput(checked = isSelected, attrs = {
            attr("tabindex", "-1")
            style {
                width(16.px); height(16.px)
                property("min-width", "16px")
                property("margin", "0")
                property("accent-color", Primary.toString())
                property("pointer-events", "none")
            }
        })
        Div(attrs = { style { width(10.px) } })
        Div(attrs = {
            style {
                fontSize(14.px)
                fontWeight(if (isSelected) 600 else 400)
                color(if (isSelected) Primary else Gray700)
            }
        }) { Text(reason.label) }
    }
}

// ── Steps 2-4: the offers ───────────────────────────────────────────────────────────────────

@Composable
private fun OfferStep(
    step: CancelStep.Offer,
    periodEndIso: String?,
    referralFreeMonthUntilIso: String?,
    loading: Boolean,
    onAccept: () -> Unit,
    onDecline: () -> Unit,
) {
    Div(attrs = {
        style {
            fontSize(22.px)
            fontWeight(700)
            color(Gray900)
            property("text-align", "center")
            property("line-height", "28px")
        }
    }) { Text(step.offer.headline) }
    Spacer(12)
    Div(attrs = {
        style {
            fontSize(14.px)
            color(Gray600)
            property("text-align", "center")
            property("line-height", "21px")
        }
    }) { Text(step.offer.body) }

    // A referral's free month still at stake: said on every step before they confirm.
    referralFreeMonthUntilIso?.let { iso ->
        Spacer(16)
        ReferralWarning(iso)
    }

    // Names the date they'd actually lose. Concrete beats abstract, and it is the one fact that
    // changes how urgent this feels.
    periodEndIso?.let { iso ->
        Spacer(16)
        Div(attrs = {
            style {
                width(100.percent)
                borderRadius(12.px)
                backgroundColor(Gray100)
                property("border", "1px solid ${Gray200}")
                padding(10.px, 14.px)
                property("box-sizing", "border-box")
                fontSize(13.px)
                color(Gray600)
                property("text-align", "center")
            }
        }) { Text("Your access runs until ${formatCancelDate(iso)}") }
    }

    Spacer(22)
    PrimaryCta(label = step.offer.acceptLabel, enabled = true, loading = loading, onClick = onAccept)
    // The last decline IS the cancellation, so it has to say so. A vague "No thanks" on the final
    // offer would cancel someone's membership without ever using the word.
    val declineLabel =
        if (step.index >= step.total) "No thanks, cancel my subscription" else "No thanks, continue cancelling"
    SecondaryLink(declineLabel, enabled = !loading, onClick = onDecline)
}

// ── Step 5a: last chance ────────────────────────────────────────────────────────────────────

@Composable
private fun FinalConfirmStep(
    periodEndIso: String?,
    referralFreeMonthUntilIso: String?,
    loading: Boolean,
    onConfirm: () -> Unit,
    onKeep: () -> Unit,
) {
    Div(attrs = {
        style {
            fontSize(20.px)
            fontWeight(700)
            color(Gray900)
            property("text-align", "center")
        }
    }) { Text("Cancel your subscription?") }
    Spacer(12)
    // First, before the date they keep: what cancelling now costs them.
    referralFreeMonthUntilIso?.let { iso ->
        ReferralWarning(iso)
        Spacer(12)
    }
    Div(attrs = {
        style {
            fontSize(14.px)
            color(Gray600)
            property("text-align", "center")
            property("line-height", "21px")
        }
    }) {
        Text(
            if (periodEndIso != null)
                "You'll keep full access until ${formatCancelDate(periodEndIso)}, the month you've " +
                    "already paid for. Nothing is deleted, and you can come back any time."
            else
                "You'll keep full access for the rest of the period you've already paid for. " +
                    "Nothing is deleted, and you can come back any time."
        )
    }
    Spacer(22)
    PrimaryCta(label = "Keep my subscription", enabled = true, loading = false, onClick = onKeep)
    Spacer(12)
    if (loading) {
        Div(attrs = {
            style {
                fontSize(13.px)
                color(Gray500)
                property("opacity", "0.6")
            }
        }) { Text("Cancelling…") }
    } else {
        SecondaryLink("Cancel my subscription", enabled = true, color = ScoreRed, onClick = onConfirm)
    }
}

// ── Step 5b: saved ──────────────────────────────────────────────────────────────────────────

@Composable
private fun SavedStep(step: CancelStep.Saved, onDone: () -> Unit) {
    // No confetti on web (the mobile version uses ConfettiKit) — the checkmark alone carries it.
    Div(attrs = {
        style {
            fontSize(48.px)
            fontWeight(700)
            color(ScoreGreen)
        }
    }) { Text("✓") }
    Spacer(14)
    Div(attrs = {
        style {
            fontSize(22.px)
            fontWeight(700)
            color(Gray900)
            property("text-align", "center")
        }
    }) { Text("You're all set") }
    Spacer(10)
    Div(attrs = {
        style {
            fontSize(14.px)
            color(Gray600)
            property("text-align", "center")
            property("line-height", "21px")
        }
    }) { Text(step.offer.confirmation) }
    step.detailIso?.let { iso ->
        Spacer(8)
        Div(attrs = {
            style {
                fontSize(13.px)
                color(Gray500)
                property("text-align", "center")
            }
        }) { Text("Next payment: ${formatCancelDate(iso)}") }
    }
    Spacer(22)
    PrimaryCta(label = "Done", enabled = true, loading = false, onClick = onDone)
}

// ── Step 5c: goodbye ────────────────────────────────────────────────────────────────────────

@Composable
private fun FarewellStep(step: CancelStep.Farewell, onDone: () -> Unit) {
    Div(attrs = {
        style {
            fontSize(20.px)
            fontWeight(700)
            color(Gray900)
            property("text-align", "center")
            property("line-height", "26px")
        }
    }) { Text("Your subscription is cancelled") }
    Spacer(12)
    Div(attrs = {
        style {
            fontSize(14.px)
            color(Gray600)
            property("text-align", "center")
            property("line-height", "21px")
        }
    }) {
        val endsAtIso = step.endsAtIso
        Text(
            if (endsAtIso != null)
                "You keep full access until ${formatCancelDate(endsAtIso)}. After that your " +
                    "account stays exactly where it is, with your saved analyses. Nothing is deleted."
            else
                "You keep full access for the rest of the period you've paid for. After that your " +
                    "account stays exactly where it is, with your saved analyses. Nothing is deleted."
        )
    }
    Spacer(16)
    Div(attrs = {
        style {
            fontSize(14.px)
            color(Gray700)
            property("text-align", "center")
            property("line-height", "21px")
        }
    }) { Text("Thank you for supporting the work. The door stays open, so come back whenever you like.") }
    Spacer(10)
    Div(attrs = {
        style {
            fontSize(12.px)
            color(Gray500)
            property("text-align", "center")
        }
    }) { Text("We've emailed you a copy of this.") }
    Spacer(22)
    PrimaryCta(label = "Close", enabled = true, loading = false, onClick = onDone)
}

// ── Shared bits ─────────────────────────────────────────────────────────────────────────────

/**
 * The referral warning: a member still in the first paid month of a referral loses the free month
 * it gave them if they cancel now. [untilIso] is when their next bill will be if they stay. Words:
 * ReferralCopy. Mirrors the mobile file's ReferralWarning.
 */
@Composable
private fun ReferralWarning(untilIso: String) {
    Div(attrs = {
        attr("role", "note")
        style {
            width(100.percent)
            borderRadius(12.px)
            backgroundColor(ScoreAmberBg)
            property("border", "1px solid #FCD34D")
            padding(12.px, 14.px)
            property("box-sizing", "border-box")
            property("text-align", "left")
        }
    }) {
        Div(attrs = {
            style {
                fontSize(14.px)
                fontWeight(700)
                color(rgb(146, 64, 14))
                property("line-height", "20px")
            }
        }) { Text(ReferralCopy.CANCEL_WARNING_TITLE) }
        Spacer(4)
        Div(attrs = {
            style {
                fontSize(13.px)
                color(Gray700)
                property("line-height", "19px")
            }
        }) { Text(ReferralCopy.cancelWarning(formatCancelDate(untilIso))) }
    }
}

@Composable
private fun PrimaryCta(label: String, enabled: Boolean, loading: Boolean, onClick: () -> Unit) {
    val disabledOrLoading = !enabled || loading
    Button(attrs = {
        if (disabledOrLoading) disabled() else onClick { onClick() }
        style {
            property("margin-top", "0px")
            width(100.percent)
            padding(14.px)
            fontSize(15.px)
            fontWeight(700)
            color(Color.white)
            property("background", GradientPrimary)
            borderRadius(14.px)
            property("border", "none")
            cursor(if (disabledOrLoading) "default" else "pointer")
            property("box-shadow", "0 6px 18px -8px rgba(14,124,123,0.7)")
            if (disabledOrLoading) opacity(0.6)
        }
    }) { Text(if (loading) "Just a moment…" else label) }
}

@Composable
private fun SecondaryLink(
    label: String,
    enabled: Boolean,
    color: CSSColorValue = Gray500,
    onClick: () -> Unit,
) {
    Div(attrs = {
        if (enabled) onClick { onClick() }
        style {
            property("margin-top", "12px")
            fontSize(13.px)
            fontWeight(500)
            color(color)
            property("text-decoration", "underline")
            cursor(if (enabled) "pointer" else "default")
        }
    }) { Text(label) }
}

/** A fixed-height spacer, mirroring the mobile file's `Spacer(Modifier.height(n.dp))` calls. */
@Composable
private fun Spacer(heightPx: Int) {
    Div(attrs = { style { height(heightPx.px) } })
}

/** "2026-10-14T09:00:00Z" → "Oct 14, 2026". Mirrors the mobile formatter (and ProfileScreen's). */
private fun formatCancelDate(iso: String): String = try {
    val date = kotlinx.datetime.LocalDate.parse(iso.substringBefore('T'))
    val month = when (date.monthNumber) {
        1 -> "Jan"; 2 -> "Feb"; 3 -> "Mar"; 4 -> "Apr"
        5 -> "May"; 6 -> "Jun"; 7 -> "Jul"; 8 -> "Aug"
        9 -> "Sep"; 10 -> "Oct"; 11 -> "Nov"; 12 -> "Dec"
        else -> "?"
    }
    "$month ${date.dayOfMonth}, ${date.year}"
} catch (_: Exception) {
    iso.substringBefore('T')
}
